/**
 * Default job-to-be-done roles that ship with the product.
 * Additive to workspace-member (auto-assigned baseline).
 *
 * Permissions listed here are DELTA only — not including workspace-member baseline.
 */

import type { PermissionAction } from './permissions';

export type JobFamilyId =
  | 'backlog'
  | 'measurement'
  | 'sessions'
  | 'cycles'
  | 'governance'
  | 'platform';

export interface JobFamilyDefinition {
  id: JobFamilyId;
  label: string;
  description: string;
}

export const JOB_FAMILIES: Record<JobFamilyId, JobFamilyDefinition> = {
  backlog: {
    id: 'backlog',
    label: 'Backlog',
    description: 'Shape what enters the system and how items connect.',
  },
  measurement: {
    id: 'measurement',
    label: 'Measurement',
    description: 'Define criteria and move values from proposal to accepted.',
  },
  sessions: {
    id: 'sessions',
    label: 'Sessions',
    description: 'Run ranking conversations — participate, facilitate, or configure.',
  },
  cycles: {
    id: 'cycles',
    label: 'Cycles',
    description: 'Own the prioritization machinery and published outputs.',
  },
  governance: {
    id: 'governance',
    label: 'Governance',
    description: 'Negotiate across the org and ratify what goes on record.',
  },
  platform: {
    id: 'platform',
    label: 'Platform',
    description: 'Read outcomes and shape how work flows at workspace level.',
  },
};

export interface JobRoleDefinition {
  slug: string;
  displayName: string;
  description: string;
  family: JobFamilyId;
  /** Delta permissions beyond workspace-member baseline */
  permissions: PermissionAction[];
}

export const DEFAULT_JOB_ROLES: JobRoleDefinition[] = [
  // ── Backlog ────────────────────────────────────────────────────────────────
  {
    slug: 'idea-submitter',
    displayName: 'Idea Submitter',
    description: 'Submit ideas through intake without doing item work.',
    family: 'backlog',
    permissions: ['items.create'],
  },
  {
    slug: 'item-manager',
    displayName: 'Item Manager',
    description:
      'Create and refine candidates; manage backlog volume, deduplication, and category routing; propose values; maintain item relationships.',
    family: 'backlog',
    permissions: [
      'items.create', 'items.edit', 'items.manage_deduplication',
      'items.categorize', 'items.request_clarity',
      'values.propose', 'relationships.create', 'criteria.view',
      'items.approve_ai_provenance',
    ],
  },
  {
    slug: 'item-triage',
    displayName: 'Item Triage',
    description:
      'Review intake queue; approve, route, attach relationships; seek clarity from requesters; transfer ownership.',
    family: 'backlog',
    permissions: [
      'items.triage', 'items.triage.approve', 'items.triage.route',
      'items.triage.attach_relationships', 'items.manage_ownership',
      'items.request_clarity',
    ],
  },
  {
    slug: 'relationship-curator',
    displayName: 'Relationship Curator',
    description: 'Maintain clusters, dependencies, packages, and lineage between items.',
    family: 'backlog',
    permissions: ['relationships.create', 'relationships.edit', 'relationships.delete'],
  },

  // ── Measurement ────────────────────────────────────────────────────────────
  {
    slug: 'criteria-authority',
    displayName: 'Criteria Authority',
    description:
      'Define criteria, their expression (numeric, T-shirt, Fibonacci, etc.), weights, and custom criterion meanings; lock and reopen criteria sets.',
    family: 'measurement',
    permissions: [
      'criteria.view', 'criteria.create', 'criteria.edit', 'criteria.select', 'criteria.weight',
      'criteria.lock', 'criteria.reopen', 'criteria.define_custom_meaning',
      'attributes.define', 'values.define_scales', 'values.define_range', 'values.approve_range',
    ],
  },
  {
    slug: 'criteria-value-authority',
    displayName: 'Criteria Value Authority',
    description:
      'Define and govern value definitions, scoring expressions, and custom criterion meanings for assigned tracks.',
    family: 'measurement',
    permissions: [
      'criteria.view', 'criteria.define_custom_meaning',
      'values.define_scales', 'values.define_range', 'values.approve_range',
      'attributes.define',
    ],
  },
  {
    slug: 'item-selection-authority',
    displayName: 'Item Selection Authority',
    description:
      'Select or remove items from consideration sets; resolve invalid item relationships when they arise.',
    family: 'measurement',
    permissions: [
      'items.select', 'items.approve_selection', 'relationships.resolve',
      'criteria.view',
    ],
  },
  {
    slug: 'values-collector',
    displayName: 'Values Collector',
    description: 'Enter attribute values during collection rounds and sessions.',
    family: 'measurement',
    permissions: ['values.propose', 'session.vote'],
  },
  {
    slug: 'values-approver',
    displayName: 'Values Approver',
    description: 'Accept, reject, converge, or override proposed values.',
    family: 'measurement',
    permissions: ['values.accept', 'values.reject', 'values.converge', 'values.override', 'ranking.override'],
  },

  // ── Sessions ───────────────────────────────────────────────────────────────
  {
    slug: 'session-participant',
    displayName: 'Session Participant',
    description: 'Participate in ranking sessions with a focused participant experience.',
    family: 'sessions',
    permissions: ['session.vote', 'session.view_results'],
  },
  {
    slug: 'session-facilitator',
    displayName: 'Session Facilitator',
    description:
      'Run live sessions; schedule and type sessions (scheduled or open); advance phases; manage pools.',
    family: 'sessions',
    // Session setup + live facilitation are one job: selecting criteria is useless
    // without the ability to define/accept each criterion's value expression
    // (range/scale), finalize the criteria set, and set values in facilitator entry
    // mode. A facilitator holding only this role was blocked mid-demo on exactly
    // those beats (2026-07 Mark demo). DB mirror: migration 634.
    permissions: [
      'session.facilitate', 'session.record', 'session.run_ranking', 'session.schedule',
      'session.set_open', 'session.pool.add', 'session.pool.remove', 'pools.select',
      'criteria.select', 'criteria.lock',
      'values.define_range', 'values.define_scales', 'values.approve_range',
      'values.propose', 'values.accept',
    ],
  },
  {
    slug: 'session-designer',
    displayName: 'Session Designer',
    description: 'Configure session setup (pool, criteria) without facilitating live.',
    family: 'sessions',
    // Setup-side twin of session-facilitator: configuring criteria requires the
    // value-expression grants and criteria.lock, but not live value entry.
    // DB mirror: migration 634.
    permissions: [
      'session.pool.add', 'session.pool.remove', 'pools.select', 'criteria.select',
      'criteria.lock', 'tracks.configure',
      'values.define_range', 'values.define_scales', 'values.approve_range',
    ],
  },
  {
    slug: 'tool-selection-authority',
    displayName: 'Tool Selection Authority',
    description:
      'Authorize which session tools are available to facilitators within their scope.',
    family: 'sessions',
    permissions: ['session.tools.select', 'session.tools.configure'],
  },

  // ── Cycles ─────────────────────────────────────────────────────────────────
  {
    slug: 'cycle-designer',
    displayName: 'Cycle Designer',
    description:
      'Create and configure prioritization cycles, tracks, and pools; design catchball paths through organizational tiers; assign workflows and decision rights.',
    family: 'cycles',
    permissions: [
      'process.create', 'process.edit', 'tracks.create', 'tracks.configure',
      'cycle.pool.add', 'cycle.pool.remove', 'workflow.use', 'workflow.assign',
      'decision_rights.assign', 'decision_rights.configure', 'pools.lock', 'pools.reopen',
    ],
  },
  {
    slug: 'local-cycle-designer',
    displayName: 'Local Cycle Designer',
    description:
      'Configure cycle elements, sessions, and pools within a delegated organizational tier.',
    family: 'cycles',
    permissions: [
      'process.edit', 'tracks.configure',
      'cycle.pool.add', 'cycle.pool.remove', 'workflow.use', 'workflow.assign',
      'decision_rights.delegate', 'pools.lock', 'pools.reopen',
    ],
  },
  {
    slug: 'track-owner',
    displayName: 'Track Owner',
    description: "Own a track — configure criteria, participants, and the track's result.",
    family: 'cycles',
    permissions: [
      'tracks.configure', 'track.set_official_result', 'criteria.select', 'criteria.lock', 'pools.lock',
    ],
  },
  {
    slug: 'priority-list-owner',
    displayName: 'Priority List Owner',
    description: 'Own published priority lists — ordering, cutline, publish.',
    family: 'cycles',
    permissions: [
      'lists.create', 'lists.edit', 'lists.move_cutline', 'lists.publish', 'process.prioritize',
    ],
  },

  // ── Governance ─────────────────────────────────────────────────────────────
  {
    slug: 'ball-holder',
    displayName: 'Ball Holder',
    description: 'Own a scope in the Cascade — assemble pools, configure sessions, propose upward.',
    family: 'governance',
    permissions: [
      'catchball.request', 'catchball.propose', 'catchball.return', 'catchball.accept_provisional',
      'catchball.rollout', 'catchball.aggregate', 'catchball.adjust_path',
      'pools.lock', 'pools.reopen', 'session.pool.add', 'session.pool.remove',
    ],
  },
  {
    slug: 'catchball-contributor',
    displayName: 'Catchball Contributor',
    description: 'Negotiate in catchball without ratification authority.',
    family: 'governance',
    permissions: ['catchball.propose', 'catchball.return', 'catchball.adjust_path'],
  },
  {
    slug: 'selection-authority',
    displayName: 'Selection Authority',
    description:
      'Exercise final selection rights over which items advance through the process at an assigned level.',
    family: 'governance',
    permissions: ['items.select', 'items.approve_selection', 'process.advance_phase'],
  },
  {
    slug: 'priority-setter',
    displayName: 'Priority Setter',
    description: 'Propose and set the priority ordering for items within an assigned level or scope.',
    family: 'governance',
    permissions: ['lists.create', 'lists.edit', 'lists.move_cutline', 'process.prioritize'],
  },
  {
    slug: 'priority-approver',
    displayName: 'Priority Approver',
    description: 'Review and approve proposed priority lists before they advance to ratification.',
    family: 'governance',
    permissions: ['lists.approve', 'process.advance_phase', 'values.override', 'ranking.override'],
  },
  {
    slug: 'decision-authority',
    displayName: 'Priority Ratifier',
    description:
      'Ratify priorities — accept/reject, publish, legitimacy gates, final overrides. Top executive or their delegate holds workspace-wide ratification rights.',
    family: 'governance',
    permissions: [
      'catchball.accept', 'catchball.reject', 'process.publish', 'process.legitimacy_advance',
      'process.advance_phase', 'session.publish', 'track.set_official_result', 'values.override', 'ranking.override',
    ],
  },
  {
    slug: 'plan-of-record-manager',
    displayName: 'Plan of Record Manager',
    description:
      'Track adherence to ratified priorities; identify and flag drift from the plan of record.',
    family: 'governance',
    permissions: [
      'analytics.view', 'analytics.export', 'session.view_results',
      'lists.view', 'process.view', 'drift.flag', 'drift.monitor',
    ],
  },

  // ── Platform ───────────────────────────────────────────────────────────────
  {
    slug: 'reports-analyst',
    displayName: 'Reports Analyst',
    description: 'View and export analytics, session results, and alignment reports.',
    family: 'platform',
    permissions: ['analytics.export', 'session.view_results'],
  },
  {
    slug: 'workflow-designer',
    displayName: 'Workflow Designer',
    description: 'Create and maintain workflow templates at workspace level.',
    family: 'platform',
    permissions: [
      'workflow.create', 'workflow.edit', 'workflow.archive', 'workflow.set_default',
      'workflow.manage_templates', 'workflow.assign',
    ],
  },
  {
    slug: 'strategist',
    displayName: 'Strategist',
    description: 'Strategic alignment — objectives, OST, alignment maps (org workspaces).',
    family: 'platform',
    permissions: ['items.create', 'analytics.export'],
  },
];

/** Roles that use the operational contributor profile (extra role links in MY WORK). */
export const ELEVATED_JOB_ROLE_SLUGS = [
  // Sessions
  'session-facilitator',
  'session-designer',
  'tool-selection-authority',
  // Cycles
  'cycle-designer',
  'local-cycle-designer',
  'track-owner',
  'priority-list-owner',
  // Backlog
  'item-manager',
  'item-triage',
  'relationship-curator',
  'item-selection-authority',
  // Measurement
  'criteria-authority',
  'criteria-value-authority',
  'values-approver',
  // Governance
  'plan-of-record-manager',
  // Platform
  'workflow-designer',
] as const;

export type ElevatedJobRoleSlug = (typeof ELEVATED_JOB_ROLE_SLUGS)[number];

const JOB_ROLE_BY_SLUG = new Map(DEFAULT_JOB_ROLES.map(r => [r.slug, r]));

export function getJobRole(slug: string): JobRoleDefinition | undefined {
  return JOB_ROLE_BY_SLUG.get(slug);
}

export function getJobRolesByFamily(family: JobFamilyId): JobRoleDefinition[] {
  return DEFAULT_JOB_ROLES.filter(r => r.family === family);
}

/** Display label for the primary job role shown in contributor shell footer. */
export function resolvePrimaryJobLabel(roleSlugs: string[]): string {
  const jobSlugs = roleSlugs.filter(s => s !== 'workspace-member' && JOB_ROLE_BY_SLUG.has(s));
  if (jobSlugs.length === 0) return 'Contributor';
  if (jobSlugs.length === 1) return JOB_ROLE_BY_SLUG.get(jobSlugs[0])!.displayName;
  const families = [...new Set(jobSlugs.map(s => JOB_ROLE_BY_SLUG.get(s)!.family))];
  if (families.length === 1) return JOB_FAMILIES[families[0]!].label;
  return `${jobSlugs.length} Jobs`;
}
